import React, { useState } from 'react';
import {
  List, Datagrid, TextField, BooleanField, DateField, FunctionField,
  Create, SimpleForm, TextInput, SelectArrayInput,
  Edit,
  Show,
  ShowButton, EditButton, DeleteButton,
  useNotify, useDataProvider, usePermissions, required, useRedirect,
  TopToolbar, CreateButton,
} from 'react-admin';
import { Box, Typography, Alert, TextField as MuiTextField, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button as MuiButton } from '@mui/material';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import { API_URL } from '../config';
import { JumpstartTextInput, JumpstartSelectArrayInput, JumpstartFormToolbar } from '../components/JumpstartInputs';
import { useT } from '../i18n/useT';

const OAUTH_SCOPE_CHOICES = [
  { id: 'read:profile', name: 'read:profile' },
  { id: 'write:profile', name: 'write:profile' },
  { id: 'read:users', name: 'read:users' },
  { id: 'write:users', name: 'write:users' },
  { id: 'write:users:password', name: 'write:users:password' },
  { id: 'read:publications', name: 'read:publications' },
  { id: 'write:publications', name: 'write:publications' },
  { id: 'read:files', name: 'read:files' },
  { id: 'write:files', name: 'write:files' },
  { id: 'read:queue', name: 'read:queue' },
  { id: 'write:queue', name: 'write:queue' },
  { id: 'admin', name: 'admin' },
];

const normalizeScopes = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map((scope) => String(scope).trim()).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((scope) => scope.trim())
      .filter(Boolean);
  }
  return [];
};

const formatScopesForDisplay = (value: unknown): string => {
  const scopes = normalizeScopes(value);
  return scopes.length > 0 ? scopes.join(', ') : '-';
};

const transformOAuthApplicationPayload = (data: any) => ({
  ...data,
  scopes: normalizeScopes(data?.scopes),
});

interface SecretDisplayDialogProps {
  open: boolean;
  data: any;
  onClose: () => void;
}

const SecretDisplayDialog: React.FC<SecretDisplayDialogProps> = ({ open, data, onClose }) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const notify = useNotify();
  const t = useT();

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    notify(t('notifications.copiedToClipboard'), { type: 'success' });
    setTimeout(() => setCopiedField(null), 2000);
  };

  if (!data) return null;

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>{t('resources.oauth-applications.secretDialog.title')}</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          <strong>{t('resources.oauth-applications.secretDialog.alertBoldLabel')}</strong>{' '}
          {t('resources.oauth-applications.secretDialog.alertText')}
        </Alert>
        <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('resources.oauth-applications.fields.client_id')}</Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <MuiTextField fullWidth value={data.client_id} InputProps={{ readOnly: true }} size="small" />
            <Tooltip title={t('actions.copy')}>
              <IconButton onClick={() => handleCopy(data.client_id, 'client_id')} size="small">
                {copiedField === 'client_id' ? '✓' : <FileCopyIcon />}
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('resources.oauth-applications.fields.client_secret')}</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <MuiTextField fullWidth value={data.client_secret} InputProps={{ readOnly: true }} type="password" size="small" />
            <Tooltip title={t('actions.copy')}>
              <IconButton onClick={() => handleCopy(data.client_secret, 'client_secret')} size="small">
                {copiedField === 'client_secret' ? '✓' : <FileCopyIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={onClose} variant="contained" color="primary">{t('actions.done')}</MuiButton>
      </DialogActions>
    </Dialog>
  );
};

const OAuthListActions = () => {
  const { permissions } = usePermissions();
  return <TopToolbar>{permissions === 'admin' && <CreateButton />}</TopToolbar>;
};

export const OAuthApplicationList = () => {
  const { permissions } = usePermissions();
  const t = useT();
  if (permissions !== 'admin') {
    return <Box sx={{ p: 3 }}><Alert severity="info">{t('resources.oauth-applications.permissionDenied.manage')}</Alert></Box>;
  }
  return (
    <List actions={<OAuthListActions />} perPage={25}>
      <Datagrid rowClick={false}>
        <TextField source="name" />
        <TextField source="client_id" />
        <FunctionField source="scopes" render={(record: any) => (
          <Typography variant="body2">{formatScopesForDisplay(record?.scopes)}</Typography>
        )} />
        <BooleanField source="is_active" />
        <DateField source="created_at" />
        <ShowButton />
        <EditButton />
        <DeleteButton />
      </Datagrid>
    </List>
  );
};

export const OAuthApplicationCreate = () => {
  const [showSecret, setShowSecret] = useState(false);
  const [secretData, setSecretData] = useState<any>(null);
  const notify = useNotify();
  const redirect = useRedirect();
  const t = useT();

  const handleSecretDialogClose = () => {
    setShowSecret(false);
    redirect('list', 'oauth-applications');
  };

  return (
    <>
      <Create
        redirect={false}
        transform={transformOAuthApplicationPayload}
        mutationOptions={{
          onSuccess: (data: any) => {
            setSecretData(data);
            setShowSecret(true);
            notify(t('resources.oauth-applications.notifications.created'), { type: 'success' });
          },
        }}
      >
        <SimpleForm toolbar={<JumpstartFormToolbar />}>
          <JumpstartTextInput
            source="name"
            label={t('resources.oauth-applications.fields.name')}
            validate={required()}
            fullWidth
          />
          <JumpstartTextInput
            source="description"
            label={t('resources.oauth-applications.fields.description')}
            multiline
            rows={3}
            fullWidth
          />
          <JumpstartSelectArrayInput
            source="scopes"
            label={t('resources.oauth-applications.fields.scopes')}
            choices={OAUTH_SCOPE_CHOICES}
            optionValue="id"
            optionText="name"
            size={8}
            validate={required()}
            helperText={t('resources.oauth-applications.helpers.scopesCreate')}
            fullWidth
          />
        </SimpleForm>
      </Create>
      <SecretDisplayDialog open={showSecret} data={secretData} onClose={handleSecretDialogClose} />
    </>
  );
};

export const OAuthApplicationEdit = () => {
  const t = useT();
  return (
    <Edit transform={transformOAuthApplicationPayload}>
      <SimpleForm toolbar={<JumpstartFormToolbar />}>
        <JumpstartTextInput
          source="name"
          label={t('resources.oauth-applications.fields.name')}
          validate={required()}
          fullWidth
        />
        <JumpstartTextInput
          source="description"
          label={t('resources.oauth-applications.fields.description')}
          multiline
          rows={3}
          fullWidth
        />
        <JumpstartSelectArrayInput
          source="scopes"
          label={t('resources.oauth-applications.fields.scopes')}
          choices={OAUTH_SCOPE_CHOICES}
          optionValue="id"
          optionText="name"
          size={8}
          validate={required()}
          helperText={t('resources.oauth-applications.helpers.scopesEdit')}
          fullWidth
        />
      </SimpleForm>
    </Edit>
  );
};

export const OAuthApplicationShow = () => {
  const { permissions } = usePermissions();
  const t = useT();
  if (permissions !== 'admin') {
    return <Box sx={{ p: 3 }}><Alert severity="info">{t('resources.oauth-applications.permissionDenied.view')}</Alert></Box>;
  }
  return (
    <Show>
      <Box sx={{ p: 3 }}>
        <TextField source="name" />
        <TextField source="client_id" />
        <FunctionField source="scopes" render={(record: any) => formatScopesForDisplay(record?.scopes)} />
        <DateField source="created_at" />
        <EditButton />
        <DeleteButton />
      </Box>
    </Show>
  );
};
